import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Package,
  Truck,
  CheckCircle,
  MapPin,
  User,
  Phone,
  Eye,
  Building2,
  Calendar,
  Clock,
} from "lucide-react";
import { Label } from "../../components/ui/label";
import {
  getPickupOrders,
  updatePickupOrderStatus,
  type PickupOrder,
} from "../../api/pickupService";
import { toast } from "sonner";
import GlobalLoader from "../../components/ui/GlobalLoader";

interface Order {
  _id: string;
  orderNumber?: string;
  prescriptionNumber?: string;
  customerName?: string;
  patientName?: string;
  customerPhone?: string;
  patientPhone?: string;
  status: string;
  location?: {
    address?: string;
  };
}

export function PickupOrdersAdminPage() {
  const [pickupOrders, setPickupOrders] = useState<PickupOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [pharmacyFilter, setPharmacyFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedPickupOrder, setSelectedPickupOrder] =
    useState<PickupOrder | null>(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<string>('');
  const [drivers, setDrivers] = useState<any[]>([]);
  const [selectedRoute, setSelectedRoute] = useState<string>('');
  const [routes, setRoutes] = useState<any[]>([]);

  // Fetch pickup orders
  const fetchPickupOrdersData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("🔍 Admin: Fetching pickup orders...");
      const response = await getPickupOrders({
        page: currentPage,
        limit: itemsPerPage,
        status: statusFilter !== "all" ? statusFilter : undefined,
      });

      if (response.success) {
        const hasDataChanged = pickupOrders.length !== response.data.length || 
          pickupOrders.some((order, index) => order._id !== response.data[index]?._id);
        
        setPickupOrders(response.data);
        console.log("✅ Admin: Pickup orders fetched successfully:", {
          count: response.data.length,
          data: response.data,
        });

        // Only show toast if data has actually changed or this is the first load
        if (hasDataChanged || pickupOrders.length === 0) {
          if (response.data.length === 0) {
            console.log("ℹ️ No pickup orders found. This could mean:");
            console.log("   - No pickup requests have been made yet");
            console.log(
              "   - Backend endpoint is working but returning empty data"
            );
            toast.info(
              "No pickup orders found. Create pickup requests from pharmacy portal first."
            );
          } else {
            // toast.success(`Loaded ${response.data.length} pickup orders`);
          }
        }
      }
    } catch (error: any) {
      console.error("❌ Error fetching pickup orders:", error);
      console.error("❌ Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      setError(error.message);

      if (error.response?.status === 404) {
        toast.error(
          "Backend endpoint not found. Please implement POST/GET /api/pickup-orders endpoints."
        );
        setError(
          "Backend endpoint not implemented yet. Check console for details."
        );
      } else if (
        error.message.includes("Network Error") ||
        error.message.includes("ECONNREFUSED")
      ) {
        // toast.error("Cannot connect to backend server. Is it running?");
        setError("Backend server is not running or not accessible.");
      } else {
        // toast.error("Failed to fetch pickup orders: " + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, []); // Only fetch drivers once on mount

  useEffect(() => {
    fetchPickupOrdersData();
  }, [currentPage, statusFilter, pharmacyFilter, priorityFilter]); // Fetch pickup orders when filters change

  const handleViewOrder = (order: any) => {
    setSelectedOrder(order);
    setViewDialogOpen(true);
  };

  const handleUpdateStatus = async (
    pickupOrderId: string,
    newStatus: string
  ) => {
    try {
      setLoading(true);
      const response = await updatePickupOrderStatus({
        pickupOrderId,
        status: newStatus,
        notes: `Status updated by admin to ${newStatus}`,
      });

      if (response.success) {
        // toast.success(`Pickup order status updated to ${newStatus}`);
        await fetchPickupOrdersData();
      } else {
        // toast.error("Failed to update status");
      }
    } catch (error: any) {
      console.error("Error updating status:", error);
      toast.error(error.message || "Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  const handleViewPickupOrder = (pickupOrder: PickupOrder) => {
    setSelectedPickupOrder(pickupOrder);
    setViewDialogOpen(true);
  };

  // Fetch available drivers
  const fetchDrivers = async () => {
    try {
      const { getAllDrivers } = await import('../../api/pharmacyService');
      const driversData = await getAllDrivers();
      console.log('Drivers data:', driversData);
      setDrivers(driversData || []);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      // Silent fail - drivers are optional for viewing orders
    }
  };

  const fetchRoutes = async () => {
    try {
      const { getAllRoutes } = await import('../../api/routeService');
      const routesData = await getAllRoutes();
      console.log('Routes data:', routesData);
      
      // Handle different response structures
      if (routesData?.data) {
        if (Array.isArray(routesData.data)) {
          setRoutes(routesData.data);
        } else if ((routesData.data as any)?.routes) {
          setRoutes((routesData.data as any).routes);
        } else {
          setRoutes([]);
        }
      } else if (Array.isArray(routesData)) {
        setRoutes(routesData);
      } else {
        setRoutes([]);
      }
    } catch (error) {
      console.error('Error fetching routes:', error);
      toast.error('Failed to load routes');
    }
  };

  // Handle assign order to rider
  const handleAssignOrder = (order: Order) => {
    setSelectedOrder(order);
    setSelectedDriver('');
    setSelectedRoute('');
    setAssignDialogOpen(true);
    fetchRoutes(); // Fetch routes when modal opens
  };

  const confirmAssignOrder = async () => {
    if (!selectedOrder || !selectedDriver) {
      toast.error('Please select a rider');
      return;
    }

    if (!selectedRoute) {
      toast.error('Please select a route');
      return;
    }

    try {
      setLoading(true);
      // Import delivery service dynamically
      const { deliveryService } = await import('../../api/deliveryService');
      
      // Get selected route name if a route was selected
      const selectedRouteObj = routes.find(r => (r.id || r._id) === selectedRoute);
      const routeNameToSend = selectedRouteObj?.name || `Route for Order ${selectedOrder.orderNumber}`;
      const routeIdToSend = selectedRouteObj?._id || '';
      await deliveryService.assignOrderToDriver(
        selectedOrder._id,
        {
          driverId: selectedDriver,
          routeName: routeNameToSend,
          routeId: routeIdToSend,
          deliveryCharges: (selectedOrder as any).deliveryCharges as number || 0
        }
      );
      
      toast.success('Order assigned to rider successfully');
      setAssignDialogOpen(false);
      setSelectedOrder(null);
      setSelectedDriver('');
      setSelectedRoute('');
      fetchPickupOrdersData();
    } catch (err) {
      console.error('Error assigning order:', err);
      toast.error('Failed to assign order to rider');
    } finally {
      setLoading(false);
    }
  };

  // Get unique pharmacies for filter
  const uniquePharmacies = Array.from(
    new Set(pickupOrders.map((po) => po.pharmacyName).filter(Boolean))
  );

  // Filter pickup orders
  const filteredPickupOrders = pickupOrders.filter((po) => {
    const term = searchTerm.trim().toLowerCase();
  
    const matchesSearch =
      !term ||
      po.pickupOrderId?.toLowerCase().includes(term) ||
      po.pharmacyName?.toLowerCase().includes(term) ||
      po.orders.some(
        (order: any) =>
          order.orderNumber?.toLowerCase().includes(term) ||
          order.customerName?.toLowerCase().includes(term)
      );
  
    const matchesPharmacy =
      pharmacyFilter === "all" || po.pharmacyName === pharmacyFilter;
  
    const matchesPriority =
      priorityFilter === "all" ||
      po.orders.some(
        (order: any) =>
          order.priority?.toLowerCase() === priorityFilter.toLowerCase()
      );
  
    return matchesSearch && matchesPharmacy && matchesPriority;
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-gray-600">
            Manage all pickup orders across pharmacies
          </p>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Pickup Orders</p>
                <p className="text-2xl font-bold">{pickupOrders.length}</p>
              </div>
                <Package className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pickup Requested</p>
                <p className="text-2xl font-bold">
                  {
                    pickupOrders.filter(
                      (po) => po.status === "pickup-requested"
                    ).length
                  }
                </p>
              </div>
                <Truck className="w-8 h-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Assigned</p>
                <p className="text-2xl font-bold">
                  {
                    pickupOrders.filter((po) => po.status === "assigned")
                      .length
                  }
                </p>
              </div>
              <Truck className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold">
                  {
                    pickupOrders.filter((po) => po.status === "completed")
                      .length
                  }
                </p>
              </div>
                <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="all" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 bg-gray-100 p-1 rounded-lg">
          <TabsTrigger value="all"
          className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50"
          >All Orders</TabsTrigger>
          <TabsTrigger value="pickup-requested"
          className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50"
          >Pickup Requested</TabsTrigger>
          <TabsTrigger value="assigned"
          className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50"
          >Assigned</TabsTrigger>
          <TabsTrigger value="completed"
          className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50"
          >Completed</TabsTrigger>
        </TabsList>

        {["all", "pickup-requested", "in-progress", "completed", "assigned"].map((tab) => (
          <TabsContent key={tab} value={tab}>
            <Card>
              <CardHeader>
                <CardTitle>
                  {tab === "all"
                    ? "All Pickup Orders"
                    : tab === "pickup-requested"
                    ? "Pickup Requested"
                    : tab === "assigned"
                    ? "Assigned Pickup Orders"
                    : tab === "in-progress"
                    ? "In Progress"
                    : "Completed Pickup Orders"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                      <Input
                        placeholder="Search by pickup order ID, pharmacy, or order number..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full h-11"
                      />
                    </div>
                    <div className="flex gap-4">
                      <Select
                        value={pharmacyFilter}
                        onValueChange={setPharmacyFilter}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Filter by Pharmacy" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Pharmacies</SelectItem>
                          {uniquePharmacies.map((pharmacy) => (
                            <SelectItem key={pharmacy} value={pharmacy || ""}>
                              {pharmacy}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={priorityFilter}
                        onValueChange={setPriorityFilter}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Filter by Priority" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Priorities</SelectItem>
                          <SelectItem value="same-day">Same Day</SelectItem>
                          <SelectItem value="next-day">Next Day</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        className="h-11"
                        onClick={() => {
                          setSearchTerm("");
                          setPharmacyFilter("all");
                          setPriorityFilter("all");
                        }}
                      >
                        Clear Filters
                      </Button>
                    </div>
                  </div>
                </div>
                        
                {/* Error State */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                        <Package className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-red-900">
                          Error Loading Pickup Orders
                        </h3>
                        <p className="text-sm text-red-700">{error}</p>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-red-200">
                      <p className="text-sm text-gray-700 mb-2">
                        <strong>Troubleshooting:</strong>
                      </p>
                      <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                        <li>Ensure backend server is running</li>
                        <li>
                          Check if{" "}
                          <code className="bg-gray-100 px-1 rounded">
                            GET /api/pickup-orders
                          </code>{" "}
                          endpoint is implemented
                        </li>
                        <li>Verify database connection is working</li>
                        <li>Check browser console for detailed error logs</li>
                      </ul>
                    </div>
                    <Button
                      onClick={fetchPickupOrdersData}
                      className="mt-4"
                      variant="outline"
                    >
                      Retry
                    </Button>
                  </div>
                )}

                {/* Pickup Orders List */}
                {!error && (
                  <div className="space-y-6">
                    {loading ? (
                      <div className="text-center py-12">
                        <GlobalLoader isLoading={true} message="Processing please wait..." subMessage="Please wait" />
                        <p className="mt-4 text-gray-600">
                          Loading pickup orders...
                        </p>
                      </div>
                    ) : filteredPickupOrders.filter(
                        (po) =>
                          tab === "all" ||
                          (tab === "pickup-requested" &&
                            po.status === "pickup-requested") ||
                          (tab === "assigned" &&
                            po.status === "assigned") ||
                          (tab === "in-progress" &&
                            po.status === "in-progress") ||
                          (tab === "completed" && po.status === "completed")
                      ).length === 0 ? (
                      <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                        <Package className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                        <p className="text-lg font-medium text-gray-700 mb-2">
                          No pickup orders found
                        </p>
                        <p className="text-sm text-gray-500 max-w-md mx-auto">
                          Pickup orders will appear here once pharmacies request
                          pickups. Visit the pharmacy portal to create pickup
                          requests.
                        </p>
                      </div>
                    ) : (
                      filteredPickupOrders
                        .filter(
                          (po) =>
                            tab === "all" ||
                            (tab === "pickup-requested" &&
                              po.status === "pickup-requested") ||
                            (tab === "assigned" &&
                              po.status === "assigned") ||
                            (tab === "in-progress" &&
                              po.status === "in-progress") ||
                            (tab === "completed" && po.status === "completed")
                        )
                        .map((pickupOrder) => (
                          
                          <Card
                            key={pickupOrder._id}
                            className="border-2 border-blue-200"
                          >
                            <CardHeader className="bg-blue-50">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <Truck className="w-6 h-6 text-blue-600" />
                                  <div>
                                    <h3 className="font-semibold text-lg flex items-center gap-2">
                                      <Badge
                                        variant="outline"
                                        className="text-2xl"
                                      >
                                        <Building2 className="w-3 h-3 mr-1" />
                                        {pickupOrder.pharmacyName ||
                                          "Unknown Pharmacy"}
                                      </Badge>
                                      Pickup Order #
                                      {pickupOrder.pickupOrderId ||
                                        pickupOrder._id.substring(0, 8)}
                                      
                                    </h3>
                                    <p className="text-sm text-gray-600 my-3">
                                      {pickupOrder.totalOrders}{" "}
                                      {pickupOrder.totalOrders === 1
                                        ? "Order"
                                        : "Orders"}{" "}
                                      • Requested{" "}
                                      {new Date(
                                        pickupOrder.requestedAt
                                      ).toLocaleString()}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge
                                    className={
                                      pickupOrder.status === "pickup-requested"
                                        ? "bg-yellow-100 text-yellow-800"
                                        : pickupOrder.status === "assigned"
                                        ? "bg-blue-100 text-blue-800"
                                        : pickupOrder.status === "picked-up"
                                        ? "bg-purple-100 text-purple-800"
                                        : pickupOrder.status === "arrived-at-facility"
                                        ? "bg-orange-100 text-orange-800"
                                        : pickupOrder.status === "completed"
                                        ? "bg-green-100 text-green-800"
                                        : "bg-gray-100 text-gray-800"
                                    }
                                  >
                                    {pickupOrder.status
                                      .replace("-", " ")
                                      .toUpperCase()}
                                  </Badge>
                                  <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                      className="h-11"
                                    onClick={() =>
                                      handleViewPickupOrder(pickupOrder)
                                    }
                                  >
                                      <Eye className="w-4 h-4 mr-1" />
                                    View Details
                                  </Button>
                                </div>
                              </div>
                                  </div>
                              {pickupOrder.rider && (
                                <div className="mt-2 flex items-center gap-2 text-sm text-gray-700">
                                  <User className="w-4 h-4" />
                                  <span>Driver: {pickupOrder.rider.name}</span>
                                  <Phone className="w-4 h-4 ml-2" />
                                      <span>{pickupOrder.rider.phone}</span>
                                </div>
                              )}
                            </CardHeader>
                            <CardContent className="pt-4">
                              {/* Nested Orders */}
                              <div className="space-y-3">
                                {pickupOrder.orders &&
                                pickupOrder.orders.length > 0 ? (
                                  pickupOrder.orders.map(
                                    (order: any, index: number) => (
                                      <div
                                        key={order._id || index}
                                        className="border rounded-lg p-4 bg-white"
                                      >
                                        <div className="space-y-3">
                                          {/* Order Header */}
                                          <div className="flex items-center justify-between pb-2 border-b">
                                            <div className="flex items-center gap-2">
                                              <Package className="w-4 h-4 text-blue-600" />
                                              <p className="font-semibold text-gray-900">
                                              {order.orderNumber ||
                                                order.prescriptionNumber ||
                                                `Order ${index + 1}`}
                                            </p>
                                            </div>
                                            <Badge
                                              variant="outline"
                                              className={`text-xs ${
                                                order.pickupStatus === 'completed' ? 'bg-green-100 text-green-800 border-green-300' :
                                                order.pickupStatus === 'picked-up' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                                                order.pickupStatus === 'pickup-requested' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                                                order.pickupStatus === 'assigned' ? 'bg-purple-100 text-purple-800 border-purple-300' :
                                                'bg-gray-100 text-gray-800 border-gray-300'
                                              }`}
                                            >
                                              {order.pickupStatus || 'Pending'}
                                            </Badge>
                                        </div>
                                        
                                          {/* Customer Info */}
                                          <div className="grid grid-cols-2 gap-3">
                                            <div>
                                              <p className="text-xs text-gray-500 mb-1">Customer</p>
                                              <div className="flex items-center gap-1">
                                                <User className="w-3 h-3 text-gray-400" />
                                                <span className="text-sm font-medium text-gray-900">
                                                {order.customerName ||
                                                  order.patientName ||
                                                  "N/A"}
                                                </span>
                                            </div>
                                            </div>
                                            <div>
                                              <p className="text-xs text-gray-500 mb-1">Phone</p>
                                              <div className="flex items-center gap-1">
                                                <Phone className="w-3 h-3 text-gray-400" />
                                                <span className="text-sm text-gray-700">
                                                {order.customerPhone ||
                                                  order.patientPhone ||
                                                  "N/A"}
                                                </span>
                                            </div>
                                          </div>
                                              </div>

                                          {/* Location */}
                                          {order.location?.address && (
                                            <div>
                                              <p className="text-xs text-gray-500 mb-1">Delivery Address</p>
                                              <div className="flex items-start gap-1">
                                                <MapPin className="w-3 h-3 text-gray-400 mt-0.5" />
                                                <span className="text-sm text-gray-700 line-clamp-2">
                                                  {order.location.address}
                                                </span>
                                              </div>
                                            </div>
                                          )}

                                          {/* Medicines Count and Priority */}
                                          <div className="flex items-center gap-2 flex-wrap">
                                            {order.medicines && order.medicines.length > 0 && (
                                              <div className="flex items-center gap-1 text-xs text-gray-600 bg-blue-50 px-2 py-1 rounded">
                                                <Package className="w-3 h-3 text-blue-600" />
                                                <span>{order.medicines.length} medicine{order.medicines.length !== 1 ? 's' : ''}</span>
                                              </div>
                                            )}
                                            {order.priority && (
                                              <div className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded ${
                                                order.priority === 'same-day' 
                                                  ? 'bg-orange-50 text-orange-700 border border-orange-200' 
                                                  : 'bg-green-50 text-green-700 border border-green-200'
                                              }`}>
                                                <Clock className="w-3 h-3" />
                                                <span>{order.priority === 'same-day' ? 'Same Day' : 'Next Day'}</span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    )
                                  )
                                ) : (
                                  <div className="text-center py-4 text-gray-500">
                                    <p className="text-sm">
                                      No orders in this pickup
                                    </p>
                                  </div>
                                )}
                              </div>

                              {/* Pickup Order Notes */}
                              {pickupOrder.notes && (
                                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                  <p className="text-sm text-gray-700">
                                    <strong>Notes:</strong> {pickupOrder.notes}
                                  </p>
                                </div>
                              )}

                              {/* Timestamps */}
                              <div className="mt-4 pt-4 border-t flex items-center gap-4 text-xs text-gray-500">
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  <span>
                                    Created:{" "}
                                      {new Date(
                                        pickupOrder.createdAt
                                      ).toLocaleDateString()}
                                  </span>
                                </div>
                                {pickupOrder.pickedUpAt && (
                                  <div className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    <span>
                                      Picked up:{" "}
                                        {new Date(
                                          pickupOrder.pickedUpAt
                                        ).toLocaleDateString()}
                                    </span>
                                  </div>
                                )}
                                {pickupOrder.completedAt && (
                                  <div className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    <span>
                                      Completed:{" "}
                                        {new Date(
                                          pickupOrder.completedAt
                                        ).toLocaleDateString()}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        ))
                    )}
                  </div>
                )}

                {/* Pagination */}
                {filteredPickupOrders.length > 0 && (
                  <div className="mt-6 flex items-center justify-between">
                    <p className="text-sm text-gray-600">
                      Showing {filteredPickupOrders.length} pickup{" "}
                      {filteredPickupOrders.length === 1 ? "order" : "orders"}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setCurrentPage((prev) => Math.max(1, prev - 1))
                        }
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage((prev) => prev + 1)}
                        disabled={filteredPickupOrders.length < itemsPerPage}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* View Pickup Order Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-6xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader className="bg-white">
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Order Details
            </DialogTitle>
            {/* <DialogDescription> */}
              {/* Complete information about order{" "} */}
              {/* {selectedPickupOrder?.pickupOrderId ||
                selectedPickupOrder?._id.substring(0, 8)} */}
            {/* </DialogDescription> */}
          </DialogHeader>
          {selectedPickupOrder && (
            <div className="space-y-6 p-4">
              {/* Pickup Order Header */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                    <div>
                    <h3 className="font-semibold text-lg">
                      <Badge
                        variant="outline"
                        className="text-2xl"
                      >
                        <Building2 className="w-3 h-3 mr-1" />
                        {selectedPickupOrder.pharmacyName ||
                          "Unknown Pharmacy"}
                      </Badge>
                        Pickup Order #
                        {selectedPickupOrder.pickupOrderId ||
                          selectedPickupOrder._id.substring(0, 8)}
                      </h3>
                    <p className="text-sm text-gray-600 my-3">
                      {selectedPickupOrder.totalOrders}{" "}
                        {selectedPickupOrder.totalOrders === 1
                          ? "Order"
                        : "Orders"}{" "}
                      • Pharmacy: {selectedPickupOrder.pharmacyName}
                      </p>
                  </div>
                  <Badge
                    className={
                      selectedPickupOrder.status === "pickup-requested"
                        ? "bg-yellow-100 text-yellow-800"
                        : selectedPickupOrder.status === "assigned"
                        ? "bg-blue-100 text-blue-800"
                        : selectedPickupOrder.status === "picked-up"
                        ? "bg-purple-100 text-purple-800"
                        : selectedPickupOrder.status === "completed"
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-800"
                    }
                  >
                    {selectedPickupOrder.status.replace("-", " ").toUpperCase()}
                  </Badge>
                </div>

                {/* Timestamps */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Requested At</p>
                    <p className="font-medium">
                      {new Date(
                        selectedPickupOrder.requestedAt
                      ).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Created At</p>
                    <p className="font-medium">
                      {new Date(selectedPickupOrder.createdAt).toLocaleString()}
                    </p>
                  </div>
                  {selectedPickupOrder.pickedUpAt && (
                    <div>
                      <p className="text-gray-500">Picked Up At</p>
                      <p className="font-medium">
                        {new Date(
                          selectedPickupOrder.pickedUpAt
                        ).toLocaleString()}
                      </p>
                    </div>
                  )}
                  {selectedPickupOrder.completedAt && (
                    <div>
                      <p className="text-gray-500">Completed At</p>
                      <p className="font-medium">
                        {new Date(
                          selectedPickupOrder.completedAt
                        ).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>

                {/* Driver Info */}
                {selectedPickupOrder.rider && (
                  <div className="mt-3 p-3 bg-white rounded border">
                    <p className="text-sm font-medium text-gray-700 mb-1">
                      Assigned Driver
                    </p>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-500" />
                      <span className="font-medium">
                        {selectedPickupOrder.rider.name}
                      </span>
                      <Phone className="w-4 h-4 text-gray-500 ml-2" />
                      <span>{selectedPickupOrder.rider.phone}</span>
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedPickupOrder.notes && (
                  <div className="mt-3 p-3 bg-yellow-50 rounded border border-yellow-200">
                    <p className="text-sm font-medium text-gray-700 mb-1">
                      Notes
                    </p>
                    <p className="text-sm text-gray-600">
                      {selectedPickupOrder.notes}
                    </p>
                  </div>
                )}
              </div>

              {/* Individual Orders */}
              <div>
                <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Orders in this Pickup (
                  {selectedPickupOrder.orders?.length || 0})
                </h4>
                <div className="space-y-4">
                  {selectedPickupOrder.orders &&
                  selectedPickupOrder.orders.length > 0 ? (
                    selectedPickupOrder.orders.map(
                      (order: any, index: number) => (
                        <div
                          key={order._id || index}
                          className="border-2 rounded-lg p-5 bg-gradient-to-br from-white to-gray-50 transition-all"
                        >
                          {/* Order Header */}
                          <div className="flex items-center justify-between mb-4 pb-3 border-b-2">
                            <div className="flex items-center gap-3">
                              <div className="bg-blue-100 p-2 rounded-lg">
                                <Package className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-bold text-gray-900 text-lg">
                                {order.orderNumber ||
                                  order.prescriptionNumber ||
                                  `Order ${index + 1}`}
                              </p>
                                <p className="text-xs text-gray-500">Order ID: {order._id?.substring(0, 12)}...</p>
                            </div>
                            </div>
                            <Badge 
                            variant="outline" 
                              className={`text-xs font-semibold px-3 py-1 ${
                                order.pickupStatus === 'completed' || order.status === 'completed' 
                                  ? 'bg-green-100 text-green-800 border-green-300' :
                                order.pickupStatus === 'picked-up' || order.status === 'picked-up'
                                  ? 'bg-blue-100 text-blue-800 border-blue-300' :
                                order.pickupStatus === 'pickup-requested' || order.status === 'pickup-requested'
                                  ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                                order.pickupStatus === 'assigned' || order.status === 'assigned'
                                  ? 'bg-purple-100 text-purple-800 border-purple-300' :
                                'bg-gray-100 text-gray-800 border-gray-300'
                              }`}
                            >
                              {order.pickupStatus || order.status || 'Pending'}
                            </Badge>
                          </div>

                          {/* Customer Information */}
                          <div className="bg-blue-50 rounded-lg p-4 mb-4">
                            <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                              <User className="w-4 h-4 text-blue-600" />
                              Customer Information
                            </h5>
                            <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-xs text-gray-500 mb-1">Name</p>
                                <p className="font-medium text-gray-900">
                                  {order.customerName || order.patientName || "N/A"}
                              </p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-1">Phone</p>
                                <div className="flex items-center gap-1">
                                  <Phone className="w-3 h-3 text-gray-400" />
                                  <p className="font-medium text-gray-900">
                                    {order.customerPhone || order.patientPhone || "N/A"}
                              </p>
                            </div>
                              </div>
                              {order.customerEmail && (
                              <div className="col-span-2">
                                  <p className="text-xs text-gray-500 mb-1">Email</p>
                                  <p className="font-medium text-gray-900">{order.customerEmail}</p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Delivery Address */}
                          {order.location?.address && (
                            <div className="bg-green-50 rounded-lg p-4 mb-4">
                              <h5 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-green-600" />
                                Delivery Address
                              </h5>
                              <p className="text-sm text-gray-700 leading-relaxed">
                                  {order.location.address}
                                </p>
                              {order.location.latitude && order.location.longitude && (
                                <p className="text-xs text-gray-500 mt-2">
                                  📍 Coordinates: {order.location.latitude.toFixed(6)}, {order.location.longitude.toFixed(6)}
                                </p>
                              )}
                              </div>
                            )}
                          
                          {/* Medicines Details */}
                            {order.medicines && order.medicines.length > 0 && (
                            <div className="bg-purple-50 rounded-lg p-4 mb-4">
                              <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                <Package className="w-4 h-4 text-purple-600" />
                                Medicines ({order.medicines.length})
                              </h5>
                              <div className="space-y-2 max-h-64 overflow-y-auto">
                                  {order.medicines.map(
                                    (med: any, medIndex: number) => (
                                    <div
                                        key={medIndex}
                                      className="bg-white rounded-lg p-3 border border-purple-200"
                                    >
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                          <p className="font-semibold text-gray-900">
                                            {medIndex + 1}. {med.name || med.medicineName}
                                          </p>
                                          <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                                            {med.quantity && (
                                              <div>
                                                <span className="text-gray-500">Quantity:</span>
                                                <span className="font-medium text-gray-900 ml-1">
                                                  {med.quantity}
                                                </span>
                                              </div>
                                            )}
                                            {(med.dosage || med.potency) && (
                                              <div>
                                                <span className="text-gray-500">Dosage:</span>
                                                <span className="font-medium text-gray-900 ml-1">
                                                  {med.dosage || med.potency}
                                                </span>
                                              </div>
                                            )}
                                            {med.type && (
                                              <div>
                                                <span className="text-gray-500">Type:</span>
                                                <span className="font-medium text-gray-900 ml-1">
                                                  {med.type}
                                                </span>
                                              </div>
                                            )}
                                            {med.manufacturer && (
                                              <div>
                                                <span className="text-gray-500">Manufacturer:</span>
                                                <span className="font-medium text-gray-900 ml-1">
                                                  {med.manufacturer}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                          {med.instructions && (
                                            <div className="mt-2 bg-yellow-50 p-2 rounded text-xs">
                                              <span className="font-medium text-yellow-800">Instructions:</span>
                                              <span className="text-yellow-700 ml-1">{med.instructions}</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    )
                                  )}
                                </div>
                              </div>
                            )}

                          {/* Additional Order Details */}
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            {order.deliveryDate && (
                              <div className="bg-gray-100 rounded p-3">
                                <p className="text-xs text-gray-500 mb-1">Delivery Date & Time</p>
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3 text-gray-600" />
                                  <p className="font-medium text-gray-900">
                                    {new Date(order.deliveryDate).toLocaleDateString()}
                                  </p>
                                </div>
                                {(order as any).deliveryTime && (
                                  <div className="flex items-center gap-1 mt-1">
                                    <Clock className="w-3 h-3 text-gray-600" />
                                    <p className="font-medium text-gray-900">
                                      {(order as any).deliveryTime}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                            {order.priority && (
                              <div className={`rounded p-3 ${
                                order.priority === 'same-day' 
                                  ? 'bg-orange-50 border border-orange-200' 
                                  : 'bg-green-50 border border-green-200'
                              }`}>
                                <p className="text-xs text-gray-500 mb-1">Delivery Priority</p>
                                <div className="flex items-center gap-1">
                                  <Clock className={`w-4 h-4 ${
                                    order.priority === 'same-day' ? 'text-orange-600' : 'text-green-600'
                                  }`} />
                                  <p className={`font-semibold ${
                                    order.priority === 'same-day' ? 'text-orange-700' : 'text-green-700'
                                  }`}>
                                    {order.priority === 'same-day' ? 'Same Day Delivery' : 'Next Day Delivery'}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Order Notes */}
                          {order.notes && (
                            <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                              <p className="text-xs font-semibold text-yellow-800 mb-1">Speciala Notes:</p>
                              <p className="text-sm text-yellow-700">{order.notes}</p>
                            </div>
                          )}
                        </div>
                      )
                    )
                  ) : (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed">
                      <Package className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm font-medium">No orders in this pickup</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end pt-4 border-t">
                {/* <div className="flex items-center gap-2">
                  <Label htmlFor="status-select">Update Status:</Label>
                  <Select
                    value={selectedPickupOrder.status}
                    onValueChange={(newStatus) => {
                      handleUpdateStatus(selectedPickupOrder._id, newStatus);
                      setViewDialogOpen(false);
                    }}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pickup-requested">
                        Pickup Requested
                      </SelectItem>
                      <SelectItem value="in-progress">In Progress</SelectItem>
                      <SelectItem value="picked-up">Picked Up</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div> */}
                <Button
                  variant="outline"
                  onClick={() => setViewDialogOpen(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Order Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Assign Order to Driver</DialogTitle>
            <DialogDescription>
              Select a rider and route to assign this order for delivery.
            </DialogDescription>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Order Details</h4>
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">Order #:</span> {selectedOrder.orderNumber || selectedOrder.prescriptionNumber}</p>
                  <p><span className="font-medium">Customer:</span> {selectedOrder.customerName || selectedOrder.patientName}</p>
                  <p><span className="font-medium">Address:</span> {selectedOrder.location?.address}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="rider">Select Driver *</Label>
                <Select value={selectedDriver} onValueChange={setSelectedDriver}>
                  <SelectTrigger id="rider">
                    <SelectValue placeholder="Choose a rider" />
                  </SelectTrigger>
                  <SelectContent>
                    {drivers?.length === 0 ? (
                      <SelectItem value="no-drivers" disabled>No drivers available</SelectItem>
                    ) : (
                      drivers?.map((rider) => (
                        <SelectItem key={rider?.id || rider?._id} value={rider?.id || rider?._id}>
                          {rider?.name} - {rider?.email}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="route">Select Route *</Label>
                <Select value={selectedRoute} onValueChange={setSelectedRoute}>
                  <SelectTrigger id="route">
                    <SelectValue placeholder="Choose a route" />
                  </SelectTrigger>
                  <SelectContent>
                    {routes?.length === 0 ? (
                      <SelectItem value="no-routes" disabled>No routes available</SelectItem>
                    ) : (
                      routes?.map((route) => (
                        <SelectItem key={route?.id || route?._id} value={route?.id || route?._id}>
                          {route?.name} ({route?.waypoints?.length || 0} stops)
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-6">
            <Button 
              variant="outline" 
              onClick={() => {
                setAssignDialogOpen(false);
                setSelectedOrder(null);
                setSelectedDriver('');
                setSelectedRoute('');
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmAssignOrder} 
              disabled={loading || !selectedDriver || !selectedRoute}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {loading ? 'Assigning...' : 'Assign Order'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
